import React from 'react';
import { useReverseGeocode } from '../hooks/useReverseGeocode';

/**
 * Displays a resolved street address for given coordinates.
 * Lat/lon shown as a subtle tooltip on hover.
 *
 * Props:
 *   lat, lon      - coordinates to resolve
 *   className     - optional wrapper class
 *   compact       - if true, uses shorter layout (for table cells)
 *   showCoords    - if true, always shows coords below the address (not just on hover)
 *   fallback      - custom fallback text while loading
 */
const AddressDisplay = ({ lat, lon, className = '', compact = false, showCoords = false, fallback }) => {
  const { address, loading } = useReverseGeocode(lat, lon);

  if (lat == null || lon == null) {
    return <span className={className}>Unknown</span>;
  }

  const coordStr = `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;

  if (loading) {
    return (
      <span className={className} title={coordStr}>
        <span className="text-gray-400 italic text-xs">{fallback || 'Resolving address...'}</span>
      </span>
    );
  }

  if (!address) {
    return <span className={className}>{coordStr}</span>;
  }

  if (compact) {
    return (
      <span className={className} title={coordStr}>
        {address}
      </span>
    );
  }

  return (
    <span className={className}>
      <span title={coordStr}>{address}</span>
      {showCoords && (
        <span className="text-gray-400 text-xs ml-1">({coordStr})</span>
      )}
    </span>
  );
};

/**
 * Displays a resolved address from a pre-resolved addressMap.
 * Used for batch-resolved coordinates (e.g., stops table).
 */
export const AddressFromMap = ({ lat, lon, addressMap, className = '' }) => {
  if (lat == null || lon == null) {
    return <span className={className}>Unknown</span>;
  }

  const key = `${Number(lat).toFixed(5)},${Number(lon).toFixed(5)}`;
  const coordStr = `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;
  const address = addressMap?.[key];

  if (!address) {
    return (
      <span className={className} title={coordStr}>
        <span className="text-gray-400 italic text-xs">Resolving...</span>
      </span>
    );
  }

  return (
    <span className={className} title={coordStr}>
      {address}
    </span>
  );
};

export default AddressDisplay;
